import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { TaskDTO } from '../models/taskDTO.model';
import { UserService } from './user.service';
import { APIResult } from '../models/APIResult.model';
import { ApiService } from './api.service';
import { Task } from '../models/task.model';
import { ShowService } from '../app/show.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { TaskCommentDto } from '../models/task-comment.dto';

/**
 * Task service.
 *
 * **Auth headers are NOT attached here.** `AuthInterceptor` adds
 * `Authorization` and `Show` to every non-public request — see the note
 * in `ApiService` and `src/app/auth/auth-interceptor.ts`. The previous
 * version had its own `buildHeaders()` and additionally attached a
 * spoofable `User-Id` header in `saveTask`; both were removed in P0.6.
 * The backend already derives the active user from the JWT, so the
 * extra header was dead code at best and a header-spoofing vector at
 * worst.
 */
@Injectable({
    providedIn: 'root'
})
export class TaskService {

    private numberOfOpenTasksSubject = new BehaviorSubject<number>(0);
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public numberOfOpenTasks$: Observable<number> = this.numberOfOpenTasksSubject.asObservable();

    private api: ApiService = inject(ApiService);
    private showService: ShowService = inject(ShowService);
    private userService: UserService = inject(UserService);
    private http: HttpClient = inject(HttpClient);

    /**
     * Get tasks, optionally filtered by show IDs.
     * @param open - Whether to only return open tasks.
     * @param showIds - Array of show IDs to filter by. If undefined, uses active show only.
     */
    public async getTasks(open: boolean = false, showIds?: number[]): Promise<any[]> {
        try {
            let url: string;

            if (showIds && showIds.length > 0) {
                // Filter by specific show IDs
                const showIdsParam = showIds.join(',');
                url = `${environment.apiUrl}/tasks?showIds=${showIdsParam}&open=${open}`;
            } else {
                // Default: filter by active show only
                const showId = await this.showService.getActiveShowId();
                url = `${environment.apiUrl}/tasks?showId=${showId}&open=${open}`;
            }

            const response = await this.http
                .get<{ data: any[] }>(url)
                .toPromise();
            return response.data;
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    public async getNumberOfOpenTasks(): Promise<void> {
        try {
            const showId = await this.showService.getActiveShowId();
            const response = await this.http
                .get<{
                    data: number;
                }>(`${ environment.apiUrl }/tasks?showId=${ showId }&open=true&amount=true`)
                .toPromise();
            this.numberOfOpenTasksSubject.next(response.data);
        } catch (error) {
            console.error(error);
            return;
        }
    }

    public async changeTaskCompletionStatus(
        taskId: number,
        completed: boolean
    ): Promise<any> {

        const activeUserId = await this.userService.getActiveUserId();

        if (activeUserId === null) {
            console.error('[TaskService] changeTaskCompletionStatus() - activeUserId is null');
            return;
        }

        try {
            let urlToCall = '';
            if (completed) {
                urlToCall = `${ environment.apiUrl }/tasks/${ taskId }/complete?completedByUserId=${ activeUserId }`;
            } else {
                urlToCall = `${ environment.apiUrl }/tasks/${ taskId }/open`;
            }
            const response = await this.http
                .patch<{ data: any }>(urlToCall, '')
                .toPromise();

            await this.getNumberOfOpenTasks();

            return response.data;
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    public async getTaskById(id: number): Promise<Task | null> {
        const found = await this.api.getById<Task>(`tasks`, id);
        if (found == null) {
            return null;
        }
        return Task.createFromJson(found);
    }

    public async getImageURLsForTaskById(taskId: number): Promise<string[]> {
        const url = environment.apiUrl + '/taskimages/' + taskId;

        try {
            const apiResult = await this.http.get<APIResult<any>>(url).toPromise();
            if (!apiResult || !apiResult.data) {
                return [];
            }
            const data = apiResult.data.map((item: any) => item.fileName);
            return data;
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    // TODO 20/07/2024 Roy Schrauwen: Moet de returntype ApiResult<Task> zijn?
    public async saveTask(task: Task): Promise<APIResult<any>> {

        // TODO 20/07/2024 Roy Schrauwen: Maak een PrepareTask() methode of iets dergelijks dat ditze twee checks (user en show) uitvoert
        const activeUserId = await this.userService.getActiveUserId() ?? null;
        if (activeUserId === null) {
            throw new Error('No active user');
        }

        const activeShowId = await this.showService.getActiveShowId() ?? null;
        if (activeShowId === null) {
            throw new Error('No active show');
        }

        const taskDTO: TaskDTO = task.convertToDTO();
        // TODO: Misschien een try-catch van maken dat als validate niet lukt, dat een error komt?
        // TODO: Should this not validate the task / taskDTO before calling the api?

        try {
            let response: APIResult<any>;
            if (task.isNew()) {
                // Creating new task
                taskDTO.submittedByUserId = activeUserId;
                response = await this.http
                    .post<APIResult<any>>(`${ environment.apiUrl }/tasks`, taskDTO)
                    .toPromise();
            } else {
                // Editing existing task
                response = await this.http
                    .put<APIResult<any>>(`${ environment.apiUrl }/tasks/${ task.id }`, taskDTO)
                    .toPromise();
            }

            // Update the number of open tasks
            this.getNumberOfOpenTasks();

            return response;
        } catch (error) {
            console.error(error);
            return null;
        }

    }

    public async getTasksForDate(date: string): Promise<any> {
        try {
            const showId = await this.showService.getActiveShowId();
            const response = await this.http
                .get<{
                    data: any[];
                }>(`${ environment.apiUrl }/tasks-for-date?showId=${ showId }&date=${ date }`)
                .toPromise();
            return response.data;
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    public async addComment(taskId: number, taskCommentDto: TaskCommentDto): Promise<any> {
        const reponse = await this.http
            .post<{ data: any[] }>(`${ environment.apiUrl }/tasks/${ taskId }/comments`,taskCommentDto)
            .toPromise();
        return reponse;
    }

}
